import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Copy, Check, X, Image as ImageIcon } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import './ViolationImageViewerPage.css';
import { useToast } from '../components/toast/ToastProvider';

const ViolationImageViewerPage: React.FC = () => {
    const { violationId } = useParams<{ violationId: string }>();
    const toast = useToast();
    const [imageUrl, setImageUrl] = useState<string>('');
    const [imageError, setImageError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [copied, setCopied] = useState<boolean>(false);
    const [imageCopied, setImageCopied] = useState<boolean>(false);
    const [pageUrl, setPageUrl] = useState<string>('');

    useEffect(() => {
        if (violationId) {
            // Construct the image URL - API_BASE_URL already includes /api/v1
            const url = `${API_BASE_URL}/violation_image/${violationId}.png`;
            setImageUrl(url);
            setPageUrl(window.location.href);
            // Do not use fetch() to probe the image: cross-origin fetch requires CORS on the
            // response, while <img> display does not. A redundant HEAD request can fail with a
            // CORS error even when the image loads fine via <img src>.
            setLoading(true);
            setImageError(false);
        }
    }, [violationId]);

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(pageUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = pageUrl;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleDownload = async () => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `violation_${violationId}.png`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Failed to download image:', err);
            toast.error('画像のダウンロードに失敗しました', { title: 'ダウンロード失敗' });
        }
    };

    const handleCopyImageToClipboard = async () => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            
            // Check if Clipboard API supports images
            if (navigator.clipboard && navigator.clipboard.write) {
                try {
                    // Try to write image to clipboard using ClipboardItem
                    const clipboardItem = new ClipboardItem({ 'image/png': blob });
                    await navigator.clipboard.write([clipboardItem]);
                    setImageCopied(true);
                    setTimeout(() => setImageCopied(false), 2000);
                } catch (clipboardError) {
                    // Fallback: convert to data URL and copy
                    console.warn('ClipboardItem not supported, using fallback:', clipboardError);
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                        try {
                            // Create an img element to copy
                            const img = new Image();
                            img.src = reader.result as string;
                            img.onload = async () => {
                                const canvas = document.createElement('canvas');
                                canvas.width = img.width;
                                canvas.height = img.height;
                                const ctx = canvas.getContext('2d');
                                if (ctx) {
                                    ctx.drawImage(img, 0, 0);
                                    canvas.toBlob(async (blob) => {
                                        if (blob) {
                                            try {
                                                const clipboardItem = new ClipboardItem({ 'image/png': blob });
                                                await navigator.clipboard.write([clipboardItem]);
                                                setImageCopied(true);
                                                setTimeout(() => setImageCopied(false), 2000);
                                            } catch (e) {
                                                console.error('Failed to copy image:', e);
                                                toast.error('画像のクリップボードへのコピーに失敗しました。このブラウザではサポートされていない可能性があります。', { title: 'コピー失敗' });
                                            }
                                        }
                                    }, 'image/png');
                                }
                            };
                        } catch (e) {
                            console.error('Failed to copy image:', e);
                            toast.error('画像のクリップボードへのコピーに失敗しました。', { title: 'コピー失敗' });
                        }
                    };
                    reader.readAsDataURL(blob);
                }
            } else {
                toast.warning('このブラウザでは画像のクリップボードへのコピーがサポートされていません。', { title: '未サポート' });
            }
        } catch (err) {
            console.error('Failed to copy image to clipboard:', err);
            toast.error('画像のクリップボードへのコピーに失敗しました。', { title: 'コピー失敗' });
        }
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.target as HTMLImageElement;
        console.error('Image load error:', e);
        console.error('Failed image URL:', imageUrl || target.src);
        console.error('Error details:', {
            naturalWidth: target.naturalWidth,
            naturalHeight: target.naturalHeight,
            complete: target.complete
        });
        setImageError(true);
        setLoading(false);
    };

    const handleImageLoad = () => {
        setLoading(false);
        setImageError(false);
    };

    if (!violationId) {
        return (
            <div className="violation-image-viewer">
                <div className="viewer-container">
                    <div className="error-message">
                        <X className="error-icon" />
                        <h2>無効なURL</h2>
                        <p>違反画像IDが指定されていません。</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="violation-image-viewer">
            <div className="viewer-container">
                <div className="viewer-header">
                    <h1>違反画像</h1>
                    <div className="action-buttons">
                        <button
                            className="action-btn copy-url-btn"
                            onClick={handleCopyToClipboard}
                            title="URLをクリップボードにコピー"
                        >
                            {copied ? (
                                <>
                                    <Check className="icon" />
                                    <span>コピーしました</span>
                                </>
                            ) : (
                                <>
                                    <Copy className="icon" />
                                    <span>URLをコピー</span>
                                </>
                            )}
                        </button>
                        <button
                            className="action-btn copy-image-btn"
                            onClick={handleCopyImageToClipboard}
                            title="画像をクリップボードにコピー"
                            disabled={!imageUrl || imageError}
                        >
                            {imageCopied ? (
                                <>
                                    <Check className="icon" />
                                    <span>画像をコピーしました</span>
                                </>
                            ) : (
                                <>
                                    <ImageIcon className="icon" />
                                    <span>画像をコピー</span>
                                </>
                            )}
                        </button>
                        <button
                            className="action-btn download-btn"
                            onClick={handleDownload}
                            title="画像をダウンロード"
                            disabled={!imageUrl || imageError}
                        >
                            <Download className="icon" />
                            <span>ダウンロード</span>
                        </button>
                    </div>
                </div>

                <div className="image-container">
                    {loading && (
                        <div className="loading-state">
                            <div className="spinner" />
                            <p>画像を読み込み中...</p>
                        </div>
                    )}
                    {imageError ? (
                        <div className="error-message">
                            <X className="error-icon" />
                            <h2>画像の読み込みに失敗しました</h2>
                            <p>指定された違反画像が見つかりませんでした。</p>
                            <p className="error-details">Violation ID: {violationId}</p>
                        </div>
                    ) : (
                        imageUrl && (
                            <img
                                src={imageUrl}
                                alt={`Violation ${violationId}`}
                                onError={handleImageError}
                                onLoad={handleImageLoad}
                                className="violation-image"
                            />
                        )
                    )}
                </div>

                <div className="viewer-footer">
                    <p className="violation-id">Violation ID: {violationId}</p>
                    <p className="page-url">{pageUrl}</p>
                </div>
            </div>
        </div>
    );
};

export default ViolationImageViewerPage;

